import './geo-filtering.scss';
import React, { useEffect, useMemo, useState } from 'react';
import { useStores } from 'src/components/store';
import { CountryRow } from './country-row/country-row';
import { observer } from 'mobx-react-lite';
import {
    BlockDirectionType,
    BulkProtectionOptions,
    GeoFilterNames,
    MainPages,
    ProtectionMenuNameOptions
} from 'src/utils/enums';
import Loader from 'src/components/shared/loader/loader';
import { CountryWithStatus } from 'src/dtos/country-with-status.dto';
import { HeaderColumnMax } from '../protection/header-column-max/header-column-max';
import { Filters } from '../../shared/filters/filters';
import { countryFiltersConfig } from '../../shared/filters/filter-config/country-filters';
import { NoResult } from '../../shared/no-result/no-result';
import { BulkPolicyUpdate } from '../../shared/bulk-options/bulk-policy-update';
import CustomButton from 'src/components/shared/custom-button/custom-button';
import { Popover } from 'antd';
import { StatusBadge } from '../../shared/status-badge/status-badge';
import { SystemConfiguration } from '@src/utils/system-configuration';
import { getCurrentFilterLabel } from '@src/utils/filters-util';
import { isEmptyMap } from '@src/utils/map-and-array';
import { usePageViewEvent } from '../../shared/hooks/use-page-view-event';

const allLetters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');
export const GeoFiltering: React.FC = observer(() => {
    const { fwVersionStore } = useStores();
    const { countryDtoMap } = SystemConfiguration.getInstance();
    const { isLoading, countriesWithStatusMap } = fwVersionStore;
    const [isShowLettersFilter, setIsShowLettersFilter] = useState(false);
    const [countriesWithStatus, setCountriesWithStatus] = useState<Record<string, CountryWithStatus[]> | undefined>(undefined);

    usePageViewEvent(MainPages.POLICY, ProtectionMenuNameOptions.GEO_FILTERING);

    useEffect(() => {
       fwVersionStore.setProtectionType(ProtectionMenuNameOptions.GEO_FILTERING);
    }, []);

    const countriesWithStatusFlatList: CountryWithStatus[] = useMemo(() => {
        const mapWithStatus: Record<string, CountryWithStatus[]> = {};
        let countriesList: CountryWithStatus[] = [];
        Object.entries(countryDtoMap || []).forEach(([key, countriesDto]) => {
            mapWithStatus[key] = countriesDto.map(countryDto => ({
                ...countryDto,
                isOutboundAllow: !(countriesWithStatusMap[countryDto.countryCode]?.[BlockDirectionType.IS_OUTBOUND]),
                isInboundAllow: !(countriesWithStatusMap[countryDto.countryCode]?.[BlockDirectionType.IS_INBOUND])
            }));
            countriesList = countriesList.concat(mapWithStatus[key]);
        });
        return countriesList;
    }, [countryDtoMap, fwVersionStore.selectedFwVersion]);

    const isAllBlockedCountriesAreHighRisk = () => {
        return countriesWithStatusFlatList.every(country =>
            (country.highRisk && !country.isOutboundAllow && !country.isInboundAllow) ||
            (!country.highRisk && country.isOutboundAllow && country.isInboundAllow));
    };

    const isRecommended = useMemo(() => {
        return isAllBlockedCountriesAreHighRisk();
    }, [countriesWithStatusFlatList]);

    const setFlatListCountryToMap = (countryList: CountryWithStatus[]) => {
        if(fwVersionStore.selectedFwVersion) {
            const groupedCountries: Record<string, CountryWithStatus[]> = {};
            countryList.forEach((country) => {
                const firstLetter = country.countryName.charAt(0);
                if (!groupedCountries[firstLetter]) {
                    groupedCountries[firstLetter] = [];
                }
                groupedCountries[firstLetter].push(country);
            });
            setCountriesWithStatus(groupedCountries);
        }
    };

    const scrollToLetter = (letter: string) => {
        const element = document.getElementById(`letter-${letter}`);
        if (!element?.parentElement) return;

        const elementRect = element.getBoundingClientRect();
        const parentRect = element.parentElement.getBoundingClientRect();
        // Calculate the scroll position to make the element visible at the bottom of the parent
        const scrollPosition = elementRect.top - parentRect.top;
        // Scroll child div without affecting parent div
        element.parentElement.scrollBy({ top: scrollPosition, behavior: 'smooth' });
    };

    const onChange = async (val: boolean, direction: BlockDirectionType, countryCode: string, countryName: string) => {
        await fwVersionStore.setIsDirty();
        const originalData = structuredClone(countriesWithStatus);
        const countryMapCopy: Record<string, CountryWithStatus[]> = { ...countriesWithStatus };
        const firstLetter = countryName.charAt(0);
        const index = countryMapCopy[firstLetter].findIndex(country => country.countryCode === countryCode);
        if (direction === BlockDirectionType.IS_INBOUND) {
            countryMapCopy[firstLetter][index].isInboundAllow = val;
        } else {
            countryMapCopy[firstLetter][index].isOutboundAllow = val;
        }
        setCountriesWithStatus(countryMapCopy);
        try {
            await fwVersionStore.updateCountryStatus(countryCode, val, direction);
        }
        catch (e) {
            setCountriesWithStatus(originalData);
        }
    };

    const filterLabel = getCurrentFilterLabel(countryFiltersConfig.localStorageKey || '', countryFiltersConfig.radioButtons?.options || []);
    const noResultsTitle = useMemo(() => {
        const isHighRisk = filterLabel !== GeoFilterNames.HighRiskCountries ? 'Countries' : '';
        return filterLabel !== GeoFilterNames.All ?
            `No ${filterLabel} ${isHighRisk}`
            : 'No Search Result';
    }, [filterLabel]);

    return <div className="page-content geo-filtering" data-testid="geo-filtering">
        <div className={`filter-wrapper ${isShowLettersFilter ? 'disapered-filters' : ''}`}>
            <Filters filterConfig={countryFiltersConfig}
                     setData={setFlatListCountryToMap}
                     originalData={countriesWithStatusFlatList}
            />
            <Popover open={isShowLettersFilter} onOpenChange={(isOpen) => setIsShowLettersFilter(isOpen)}
                overlayClassName="letters-filter-popover" placement="left"
                showArrow={false} content={<div className={'scroll-to-letter'}>
                    {allLetters.map(letter =>
                        <button type="button" key={letter} className={`letter ${countriesWithStatus?.[letter] ? '' : 'disable'}`}
                            disabled={!countriesWithStatus?.[letter]} onClick={() => scrollToLetter(letter)}>{letter}</button>)}
                </div>}>
                <CustomButton label="A - Z" className="secondary-btn letters-filter" />
            </Popover>
            <div className="right-actions">
                {countriesWithStatusFlatList.length > 0 &&
                    <StatusBadge isRecommended={isRecommended} pageName={BulkProtectionOptions.GEO_FILTERING} />}
                <BulkPolicyUpdate tab={BulkProtectionOptions.GEO_FILTERING} />
            </div>
        </div>
        <div className="page-content-wrapper" data-testid="page-content">
            {isLoading && <Loader type="full-screen" />}
                {countriesWithStatus && !isEmptyMap(countriesWithStatus) && <>
                    <HeaderColumnMax />
                    <div className={'countries-wrapper'}>
                        {Object.entries(countriesWithStatus).map(([key, countriesDto]) =>
                            <div className="countries-group" key={key} id={`letter-${key}`}
                                 data-testid={`countries-group-${key}`}>
                                <div className={`first-letter letter-${key}`}>{key}</div>
                                <div className="countries">{countriesDto.map(countryWithStatus =>
                                    <CountryRow key={countryWithStatus.countryCode}
                                                countryWithStatus={countryWithStatus}
                                                isOutboundAllow={countryWithStatus.isOutboundAllow}
                                                isInboundAllow={countryWithStatus.isInboundAllow}
                                                onChange={onChange}/>)}
                                </div>
                            </div>)}
                    </div>
                </>}
                {countriesWithStatus && isEmptyMap(countriesWithStatus) && <NoResult title={noResultsTitle}/>}
        </div>
    </div>;
});